import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import equal from 'deep-equal';

import {
  MARITAL_STATUS_IDS,
  KEY_COMPONENTS,
} from 'components/modules/MainAnketa/MainAnketa.constants';
import { MIDDLE_NAME_KEYS, MOBILE_PHONE_DEFAULT_VALUE, NAMES } from './PersonalForm.constants';
import { FIO_CHANGE_REASON_MARRIED } from 'store/anketa/mappers/personalInfo';
import {
  formatFioToUpperCase,
  formatPrevFioToUpperCase,
  hasFioFieldsChanged,
  hasNameChanged,
} from './PersonalForm.helpers';

export const usePersonalFormPresenter = ({
  persona,
  validate,
  errors,
  confidenceErrors,
  clearConfidenceErrors,
  setFocusAt,
  onChange,
}) => {
  const fullNameRef = useRef(null);

  const { fiasId, regDate } = persona.addressReg;

  const { martialStatusesList, educationList } = useSelector((state) => state.referenceBooks);

  const [localErrors, setLocalErrors] = useState({});
  const [personalFormValues, setPersonalFormValues] = useState(null);
  const [contactsFormValues, setContactsFormValues] = useState(null);
  const [workFormValues, setWorkFormValues] = useState(null);
  const [activeAlert, setActiveAlert] = useState(null);
  const [middleAlert, setMiddleAlert] = useState({ middleName: true, prevMiddleName: true });

  const changeHandler = useCallback(
    (formValue) => {
      setPersonalFormValues((previousFormValues) => {
        const mergedFormValues = Array.isArray(formValue)
          ? formValue.reduce((acc, value) => ({ ...acc, ...value }), { ...previousFormValues })
          : { ...previousFormValues, ...formValue };

        const withFamilyState = formValue[NAMES.PERSON_MARITAL_STATUS]
          ? {
              ...mergedFormValues,
              yearsMarried: formValue.familyState.id === MARITAL_STATUS_IDS.MARRIED_PERSON ? 1 : 0,
            }
          : mergedFormValues;

        const finalFormValues =
          formValue[NAMES.PERSON_FIO_CHANGED] !== undefined
            ? {
                ...withFamilyState,
                changeFioReason: formValue[NAMES.PERSON_FIO_CHANGED]
                  ? FIO_CHANGE_REASON_MARRIED
                  : {},
              }
            : withFamilyState;

        onChange({
          values: finalFormValues,
          id: persona.id,
          key: KEY_COMPONENTS.PERSONAL_INFO_FORM,
        });

        return finalFormValues;
      });

      if (hasFioFieldsChanged(formValue)) {
        setMiddleAlert((prev) => {
          const updated = {};

          for (const key in prev) {
            updated[key] = true;
          }

          return updated;
        });
      }
    },
    [persona.id, onChange]
  );

  const changeHandlerFio = useCallback(
    (formValue) => {
      const formattedFio = formatFioToUpperCase(formValue);

      changeHandler(formattedFio);
    },
    [changeHandler]
  );

  const changeHandlerPrevFio = useCallback(
    (formValue) => {
      const formattedFio = formatPrevFioToUpperCase(formValue);

      changeHandler(formattedFio);
    },
    [changeHandler]
  );

  const changeHandlerFioCheckbox = useCallback(() => {
    changeHandler({ [NAMES.PERSON_FIO_CHANGED]: !personalFormValues[NAMES.PERSON_FIO_CHANGED] });

    if (!personalFormValues[NAMES.PERSON_FIO_CHANGED]) {
      setFocusAt(`${KEY_COMPONENTS.PERSONAL_INFO_FORM}_${NAMES.PREVIOUS_PERSON_FULL_NAME}_input`);
    }
  }, [setFocusAt, changeHandler]);

  const changeHandlerContacts = useCallback(
    (formValue) => {
      setContactsFormValues((previousFormValues) => {
        const mergedValues = Array.isArray(formValue)
          ? formValue.reduce((acc, value) => ({ ...acc, ...value }), { ...previousFormValues })
          : { ...previousFormValues, ...formValue };

        onChange({
          values: mergedValues,
          id: persona.id,
          key: KEY_COMPONENTS.CONTACTS,
        });

        return mergedValues;
      });
    },
    [persona.id, onChange]
  );

  const changeHandlerPhone = useCallback(
    (formValue) => {
      const normalizedValue = formValue[NAMES.PERSON_MOBILE_PHONE];

      return normalizedValue === MOBILE_PHONE_DEFAULT_VALUE
        ? changeHandlerContacts({ [NAMES.PERSON_MOBILE_PHONE]: '' })
        : changeHandlerContacts({ [NAMES.PERSON_MOBILE_PHONE]: normalizedValue });
    },
    [changeHandlerContacts]
  );

  const changeHandlerChildrenCount = useCallback(
    (formValue) => {
      const childrenCount = formValue[NAMES.PERSON_CHILDREN_COUNT] ?? 0;

      changeHandler({
        [NAMES.PERSON_CHILDREN_COUNT]: childrenCount,
        [NAMES.PERSON_DEPENDENT_COUNT]: childrenCount,
      });
    },
    [changeHandler]
  );

  const changeHandlerEducation = useCallback(
    (formValue) => {
      setWorkFormValues((previousFormValues) => {
        const mergedWorkValues = Array.isArray(formValue)
          ? formValue.reduce((acc, value) => ({ ...acc, ...value }), { ...previousFormValues })
          : { ...previousFormValues, ...formValue };

        onChange({
          values: mergedWorkValues,
          id: persona.id,
          key: KEY_COMPONENTS.WORK,
        });

        return mergedWorkValues;
      });
    },
    [persona.id, onChange]
  );

  const validation = useCallback(
    (path) => {
      setLocalErrors((prev) => ({ ...prev, [path]: errors[path] || null }));
      clearConfidenceErrors(path);
    },
    [errors, clearConfidenceErrors]
  );

  const startExtendedValidation = useCallback(
    (path, middleNameKey) => {
      validation(path);

      const hasFirstName = personalFormValues?.firstName?.trim().length > 0;
      const hasLastName = personalFormValues?.lastName?.trim().length > 0;
      const isEmptyMiddleName = !personalFormValues?.[middleNameKey]?.trim();
      const hasMiddleAlert = middleAlert[middleNameKey];
      const hasNoMiddleNameFlag = personalFormValues?.noMiddle;

      const shouldShowPopup =
        hasFirstName && hasLastName && isEmptyMiddleName && hasMiddleAlert && !hasNoMiddleNameFlag;

      if (shouldShowPopup) {
        setActiveAlert(middleNameKey);
      }
    },
    [validation, personalFormValues, middleAlert]
  );

  const deactivateCurrentMiddleAlert = () => {
    setMiddleAlert((prev) => ({
      ...prev,
      [activeAlert]: false,
    }));
  };

  const handleMiddleNameAgree = useCallback(() => {
    if (!activeAlert) {
      return;
    }

    const keys =
      activeAlert === NAMES.PREVIOUS_PERSON_MIDDLE_NAME
        ? MIDDLE_NAME_KEYS.PREVIOUS
        : MIDDLE_NAME_KEYS.CURRENT;

    changeHandler((prevFormValues) => {
      const currentFullName = prevFormValues[keys.fullName] || '';
      const nameParts = currentFullName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

      const newFullName = `${firstName} ${lastName}`.trim();

      return {
        [keys.value]: '',
        [keys.fullName]: newFullName,
        noMiddle: true,
      };
    });

    deactivateCurrentMiddleAlert();
    setActiveAlert(null);
  }, [activeAlert, changeHandler]);

  const handleMiddleNameCancel = useCallback(() => {
    if (!activeAlert) {
      return;
    }

    deactivateCurrentMiddleAlert();
    setActiveAlert(null);
  }, [activeAlert]);

  const handleFioAgree = useCallback(() => {
    fullNameRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    deactivateCurrentMiddleAlert();
    setActiveAlert(null);
  }, []);

  const getError = useCallback(
    (path) => {
      const validationError = errors[path];
      const generalError = localErrors[path];

      if (!validate) {
        return generalError;
      }

      return validationError ?? generalError;
    },
    [validate, errors, localErrors]
  );

  const getConfidenceError = useCallback(
    (path) => {
      return Object.hasOwn(confidenceErrors, path);
    },
    [confidenceErrors]
  );

  useEffect(() => {
    if (!persona || equal(personalFormValues, persona[KEY_COMPONENTS.PERSONAL_INFO_FORM])) {
      return;
    }

    const { lastName, firstName, middleName } = persona[KEY_COMPONENTS.PERSONAL_INFO_FORM];

    if (personalFormValues) {
      const isNameChanged = hasNameChanged(personalFormValues, { lastName, firstName, middleName });

      if (isNameChanged) {
        setActiveAlert('fio');
      }
    } else {
      if (!persona.applicationParams.lastModifiedAt) {
        setMiddleAlert(
          Object.keys(middleAlert).reduce((obj, key) => ({ ...obj, [key]: false }), {})
        );
      }
    }

    setPersonalFormValues(persona[KEY_COMPONENTS.PERSONAL_INFO_FORM]);
  }, [persona]);

  useEffect(() => {
    if (!persona || equal(contactsFormValues, persona[KEY_COMPONENTS.CONTACTS])) {
      return;
    }

    setContactsFormValues(persona[KEY_COMPONENTS.CONTACTS]);
  }, [persona]);

  useEffect(() => {
    if (!persona || equal(workFormValues, persona[KEY_COMPONENTS.WORK])) {
      return;
    }

    setWorkFormValues(persona[KEY_COMPONENTS.WORK]);
  }, [persona]);

  useEffect(() => {
    setLocalErrors(
      Object.keys(localErrors).reduce((obj, key) => ({ ...obj, [key]: errors[key] || null }), {})
    );
  }, [errors]);

  useEffect(() => {
    if (
      (fiasId || regDate) &&
      personalFormValues?.[NAMES.PERSON_MARITAL_STATUS].id === MARITAL_STATUS_IDS.NO_STATE
    ) {
      validation(`${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_MARITAL_STATUS}.sysName`);
    }
  }, [fiasId, regDate]);

  return {
    fullNameRef,
    activeAlert,
    educationList,
    workFormValues,
    personalFormValues,
    contactsFormValues,
    martialStatusesList,
    getError,
    validation,
    changeHandler,
    handleFioAgree,
    changeHandlerFio,
    getConfidenceError,
    changeHandlerPhone,
    changeHandlerPrevFio,
    handleMiddleNameAgree,
    changeHandlerEducation,
    handleMiddleNameCancel,
    startExtendedValidation,
    changeHandlerFioCheckbox,
    changeHandlerChildrenCount,
  };
};
